/**
 * InvoiceDetailPage 發票明細 (S7.1) — `/auth/finance/invoices/:kind/:invoiceId`.
 *
 * Read-only record view for both invoice kinds:
 * - Customer 客戶發票 — shows the invoice-on-loaded provenance (依已裝量開立
 *   basis tag + per-line loaded-at-generation column) and drills to the
 *   linked SO / POs.
 * - Vendor 供應商發票 — shows the manual-intake facts and the synthesized
 *   three-way match rows (poQty / receivedQty / invoicedQty / variance).
 *
 * Drill-through only — invoicing writes happen on the list page's modals;
 * OCC and the S8.1 AR/AP pages read the same store (one record, many lenses).
 */

import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { Button, Descriptions, Skeleton, Table, Tag, Typography, theme } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useRequest } from 'ahooks';
import PageHeader from '@/components/common/PageHeader';
import TableContainer, { ListEmptyState, renderDash } from '@/components/common/TableContainer';
import { useElevation } from '@/styles/useElevation';
import apiClient from '@/services/api/axios.config';
import type {
  ApInvoiceStatus,
  ArInvoiceStatus,
  CustomerInvoice,
  CustomerInvoiceLine,
  VendorInvoice,
} from '@/types/api/finance';
import { formatMoney } from '../salesOrders/components/soFormat';
import type { InvoiceKind } from './InvoiceListPage';
import PageContainer from '@/components/common/PageContainer';

const { Text } = Typography;

const CUSTOMER_STATUS_COLOR: Record<ArInvoiceStatus, string> = {
  draft: 'default',
  open: 'blue',
  partial: 'orange',
  paid: 'green',
  void: 'red',
};

const VENDOR_STATUS_COLOR: Record<ApInvoiceStatus, string> = {
  draft: 'default',
  matched: 'blue',
  pending_approval: 'orange',
  approved: 'cyan',
  paid: 'green',
  held: 'red',
};

const THREE_WAY_COLOR: Record<'pass' | 'fail' | 'pending', string> = {
  pass: 'green',
  fail: 'red',
  pending: 'orange',
};

type InvoiceDetail =
  | { kind: 'customer'; invoice: CustomerInvoice }
  | { kind: 'vendor'; invoice: VendorInvoice };

const InvoiceDetailPage = () => {
  const navigate = useNavigate();
  const { kind: kindParam, invoiceId } = useParams<{ kind: string; invoiceId: string }>();
  const { t } = useTranslation(['finance', 'common']);
  const { token } = theme.useToken();
  const elevation = useElevation();

  const kind: InvoiceKind | null =
    kindParam === 'customer' || kindParam === 'vendor' ? kindParam : null;

  const {
    data: detail,
    loading,
    error,
    refresh,
  } = useRequest(
    async (): Promise<InvoiceDetail | null> => {
      if (!kind || !invoiceId) return null;
      if (kind === 'customer') {
        const { data } = await apiClient.get<{ data: CustomerInvoice }>(
          `/finance/customer-invoices/${invoiceId}`
        );
        return { kind, invoice: data.data };
      }
      const { data } = await apiClient.get<{ data: VendorInvoice }>(
        `/finance/vendor-invoices/${invoiceId}`
      );
      return { kind, invoice: data.data };
    },
    {
      cacheKey: `/api/finance/${kindParam}-invoices/${invoiceId}`,
      refreshDeps: [kindParam, invoiceId],
    }
  );

  if (!kind) {
    return <Navigate to="/auth/finance/invoices/customer" replace />;
  }

  const backToList = () => navigate(`/auth/finance/invoices/${kind}`);

  if (error) {
    return (
      <PageContainer>
        <TableContainer
          errorState={{
            active: true,
            title: t('finance:invoices.states.notFound'),
            retryLabel: t('common:errors.boundary.retry'),
            onRetry: refresh,
          }}
        >
          <ListEmptyState
            title={t('finance:invoices.states.notFound')}
            actionLabel={t('finance:invoices.actions.backToList')}
            onAction={backToList}
            actionType="default"
          />
        </TableContainer>
      </PageContainer>
    );
  }

  if (loading || !detail) {
    return (
      <PageContainer>
        <Skeleton active paragraph={{ rows: 8 }} className="tw-mt-6" />
      </PageContainer>
    );
  }

  const statusNs = detail.kind === 'customer' ? 'statusCustomer' : 'statusVendor';
  const statusColor =
    detail.kind === 'customer'
      ? CUSTOMER_STATUS_COLOR[detail.invoice.status]
      : VENDOR_STATUS_COLOR[detail.invoice.status];

  const sectionCardStyle: React.CSSProperties = {
    backgroundColor: token.colorBgContainer,
    ...elevation.card,
    padding: '24px',
    marginBottom: '24px',
  };

  return (
    <PageContainer>
      <PageHeader
        title={detail.invoice.invoiceNumber}
        extra={
          <div className="tw-flex tw-items-center tw-gap-3">
            <Tag color={statusColor} style={{ margin: 0 }}>
              {t(`finance:invoices.${statusNs}.${detail.invoice.status}`)}
            </Tag>
            <Button icon={<ArrowLeftOutlined />} size="large" onClick={backToList}>
              {t('finance:invoices.actions.backToList')}
            </Button>
          </div>
        }
      />

      {/* Summary */}
      <div style={sectionCardStyle}>
        <Descriptions
          bordered
          column={2}
          size="middle"
          title={t('finance:invoices.detail.summarySection')}
        >
          {detail.kind === 'customer' ? (
            <Descriptions.Item label={t('finance:invoices.fields.customer')}>
              {detail.invoice.customerName}
            </Descriptions.Item>
          ) : (
            <Descriptions.Item label={t('finance:invoices.fields.vendor')}>
              {detail.invoice.vendorName}
            </Descriptions.Item>
          )}
          <Descriptions.Item label={t('finance:invoices.fields.amount')}>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>
              {formatMoney(detail.invoice.currency, detail.invoice.amount)}
            </span>
          </Descriptions.Item>
          {detail.kind === 'customer' && (
            <>
              <Descriptions.Item label={t('finance:invoices.fields.paidAmount')}>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {formatMoney(detail.invoice.currency, detail.invoice.paidAmount)}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label={t('finance:invoices.fields.outstanding')}>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {formatMoney(
                    detail.invoice.currency,
                    Math.round((detail.invoice.amount - detail.invoice.paidAmount) * 100) / 100
                  )}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label={t('finance:invoices.fields.container')}>
                {detail.invoice.containerNumber || '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('finance:invoices.fields.issuedAt')}>
                {detail.invoice.issuedAt ? detail.invoice.issuedAt.slice(0, 10) : '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('finance:invoices.fields.basis')}>
                <Tag color="purple" style={{ margin: 0 }}>
                  {t('finance:invoices.basisLoaded')}
                </Tag>
              </Descriptions.Item>
            </>
          )}
          {detail.kind === 'vendor' && (
            <>
              <Descriptions.Item label={t('finance:invoices.fields.invoiceDate')}>
                {detail.invoice.invoiceDate}
              </Descriptions.Item>
              <Descriptions.Item label={t('finance:invoices.fields.threeWay')}>
                <Tag color={THREE_WAY_COLOR[detail.invoice.threeWayMatch]} style={{ margin: 0 }}>
                  {t(`finance:invoices.threeWay.${detail.invoice.threeWayMatch}`)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('finance:invoices.fields.paymentTerms')}>
                {detail.invoice.paymentTerms || '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('finance:invoices.fields.enteredBy')}>
                {detail.invoice.enteredBy || '-'}
                <Text type="secondary" style={{ marginLeft: 8, fontSize: token.fontSizeSM }}>
                  {t('finance:invoices.detail.intakeManual')}
                </Text>
              </Descriptions.Item>
            </>
          )}
          <Descriptions.Item label={t('finance:invoices.fields.dueDate')}>
            {detail.invoice.dueDate || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('finance:invoices.fields.currency')}>
            {detail.invoice.currency}
          </Descriptions.Item>
        </Descriptions>
        {detail.kind === 'customer' && (
          <Text
            type="secondary"
            style={{ display: 'block', marginTop: '12px', fontSize: token.fontSizeSM }}
          >
            {t('finance:invoices.basisLoadedHint')}
          </Text>
        )}
      </div>

      {/* Linked documents — drill to source (invoiced flags live on those pages) */}
      <div style={sectionCardStyle}>
        <Text strong style={{ display: 'block', marginBottom: '12px' }}>
          {t('finance:invoices.detail.linksSection')}
        </Text>
        <div className="tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
          {detail.kind === 'customer' && detail.invoice.soId && (
            <Button
              size="large"
              onClick={() =>
                navigate(`/auth/sales-orders/${(detail.invoice as CustomerInvoice).soId}`)
              }
            >
              {t('finance:invoices.actions.viewSo')}
              {detail.invoice.soNumber ? ` — ${detail.invoice.soNumber}` : ''}
            </Button>
          )}
          {detail.kind === 'customer' &&
            detail.invoice.poIds.map((poId) => (
              <Button key={poId} size="large" onClick={() => navigate(`/auth/purchase-orders/${poId}`)}>
                {t('finance:invoices.actions.viewPo')}
              </Button>
            ))}
          {detail.kind === 'vendor' && detail.invoice.poId && (
            <Button
              size="large"
              onClick={() =>
                navigate(`/auth/purchase-orders/${(detail.invoice as VendorInvoice).poId}`)
              }
            >
              {t('finance:invoices.actions.viewPo')}
              {detail.invoice.poNumber ? ` — ${detail.invoice.poNumber}` : ''}
            </Button>
          )}
          {detail.kind === 'vendor' && detail.invoice.soId && (
            <Button
              size="large"
              onClick={() =>
                navigate(`/auth/sales-orders/${(detail.invoice as VendorInvoice).soId}`)
              }
            >
              {t('finance:invoices.actions.viewSo')}
              {detail.invoice.soNumber ? ` — ${detail.invoice.soNumber}` : ''}
            </Button>
          )}
          <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
            {t('finance:invoices.detail.fulfillmentLines', {
              count: detail.invoice.fulfillmentLineIds.length,
            })}
          </Text>
        </div>
      </div>

      {/* Lines */}
      {detail.kind === 'customer' ? (
        <div style={sectionCardStyle}>
          <Text strong style={{ display: 'block', marginBottom: '12px' }}>
            {t('finance:invoices.detail.linesSection')}
          </Text>
          <TableContainer>
            <Table<CustomerInvoiceLine>
              size="small"
              rowKey="id"
              dataSource={detail.invoice.lines}
              pagination={false}
              columns={[
                {
                  title: t('finance:invoices.fields.product'),
                  key: 'product',
                  render: (_: unknown, line) => (
                    <div className="tw-flex tw-flex-col tw-gap-0.5">
                      <span style={{ fontWeight: token.fontWeightStrong }}>{line.sku}</span>
                      <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
                        {line.productName}
                      </span>
                    </div>
                  ),
                },
                {
                  title: t('finance:invoices.fields.qtyLoaded'),
                  dataIndex: 'qtyLoadedAtGeneration',
                  key: 'qtyLoadedAtGeneration',
                  align: 'right',
                  render: (value: number) => (
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</span>
                  ),
                },
                {
                  title: t('finance:invoices.fields.qty'),
                  dataIndex: 'qty',
                  key: 'qty',
                  align: 'right',
                  render: (value: number) => (
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</span>
                  ),
                },
                {
                  title: t('finance:invoices.fields.unitPrice'),
                  dataIndex: 'unitPrice',
                  key: 'unitPrice',
                  align: 'right',
                  render: (value: number) => (
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {formatMoney(detail.invoice.currency, value)}
                    </span>
                  ),
                },
                {
                  title: t('finance:invoices.fields.lineAmount'),
                  dataIndex: 'amount',
                  key: 'amount',
                  align: 'right',
                  render: (value: number) => (
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {formatMoney(detail.invoice.currency, value)}
                    </span>
                  ),
                },
              ]}
            />
          </TableContainer>
        </div>
      ) : (
        <div style={sectionCardStyle}>
          <Text strong style={{ display: 'block', marginBottom: '12px' }}>
            {t('finance:invoices.detail.matchSection')}
          </Text>
          <TableContainer>
            <Table<VendorInvoice['matchLines'][number]>
              size="small"
              rowKey="poLineId"
              dataSource={detail.invoice.matchLines}
              pagination={false}
              columns={[
                {
                  title: t('finance:invoices.fields.product'),
                  dataIndex: 'sku',
                  key: 'sku',
                  render: (value: string) => (
                    <span style={{ fontWeight: token.fontWeightStrong }}>{value}</span>
                  ),
                },
                {
                  title: t('finance:invoices.fields.qty'),
                  dataIndex: 'poQty',
                  key: 'poQty',
                  align: 'right',
                  render: (value: number) => (
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</span>
                  ),
                },
                {
                  title: t('finance:invoices.fields.qtyInvoiced'),
                  dataIndex: 'invoicedQty',
                  key: 'invoicedQty',
                  align: 'right',
                  render: (value: number) => (
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</span>
                  ),
                },
                {
                  title: t('finance:invoices.fields.lineAmount'),
                  dataIndex: 'invoicedAmount',
                  key: 'invoicedAmount',
                  align: 'right',
                  render: (value: number) => (
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {formatMoney(detail.invoice.currency, value)}
                    </span>
                  ),
                },
                {
                  title: t('finance:invoices.fields.variance'),
                  dataIndex: 'varianceAmount',
                  key: 'varianceAmount',
                  align: 'right',
                  render: (value: number) =>
                    value === 0 ? (
                      renderDash()
                    ) : (
                      <span
                        style={{ fontVariantNumeric: 'tabular-nums', color: token.colorError }}
                      >
                        {formatMoney(detail.invoice.currency, value)}
                      </span>
                    ),
                },
              ]}
            />
          </TableContainer>
        </div>
      )}
    </PageContainer>
  );
};

export default InvoiceDetailPage;
